import React from 'react'
import { CloseIcon, SpinnerIcon } from '@slite/icons'

import { urls } from '../urls'

import {
  Layout,
  Text,
  HorizontalRule,
  Main,
  ButtonHTMLLink,
} from '../../../components'

import { Paths } from '../constants'
import { usePathMetadata } from '../../../hooks/usePathMetadata'
import { appProtocol, appDomain, appPath, superPath, superDomain } from 'config'
import { useShowJoinedOrganizationsQuery } from 'remote/graphql'
import { SelectByDomain } from './ByDomain'
import { SelectByEmail } from './ByEmail'
import { Avatar } from '../../../components/Avatar'
import { FormattedMessage } from 'react-intl'
import { useDiscardOrg } from './hooks/useDiscardOrg'
import { getStructureCacheKeyFromUrl, isAskXDesktop } from '../../../utils'
import { useHistory } from 'react-router'
import { getItemSafe, setItemSafe } from '../../../helpers/localStorage'

export function OrganizationSelect({
  superEnabled,
}: {
  superEnabled: boolean
}) {
  const [isNavigating, setIsNavigating] = React.useState(false)
  //  Specifically when Zapier is used, the user can 4x click and land on the wrong place, so we put a disabled state

  const { data } = useShowJoinedOrganizationsQuery()
  const { discardOrg, discardedOrgIds } = useDiscardOrg()
  const organizations =
    data?.showJoinedOrganizations.filter(
      (org) => !discardedOrgIds.includes(org.id)
    ) ?? []

  const Metadata = usePathMetadata(Paths.Select)
  const history = useHistory()
  const hasPreviousLocation = history.length > 1
  const lastJoinedAskXDomain = getItemSafe('askxsubdomain')
  const structureCacheKey = getStructureCacheKeyFromUrl()
  const shouldRedirectToAskxApp =
    isAskXDesktop() && lastJoinedAskXDomain && !hasPreviousLocation
  const productDomain = superEnabled ? superDomain : appDomain

  React.useEffect(() => {
    if (shouldRedirectToAskxApp) {
      window.location.href = urls.getOrganizationAppPath(
        {
          appDomain: productDomain,
          appProtocol,
          appPath: superEnabled ? superPath : appPath,
        },
        lastJoinedAskXDomain
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirectToAskxApp])

  const Footer = (
    <Text.SmallDetail className="margin-top-32" style={{ alignSelf: 'center' }}>
      <FormattedMessage
        id="boarding.signIn.footer"
        defaultMessage="Need to get your team on {product}? <link>Create a new team</link>"
        values={{
          link: (text: React.ReactNode) => (
            <Text.ExternalLink
              inline
              to={
                superEnabled
                  ? '/organization/super/create'
                  : '/organization/create'
              }
            >
              {text}
            </Text.ExternalLink>
          ),
          product: superEnabled ? 'Super' : 'Slite',
        }}
      />
    </Text.SmallDetail>
  )
  if (shouldRedirectToAskxApp) {
    return null
  }

  return (
    <>
      <Metadata />
      <Layout superEnabled={superEnabled}>
        <Main.Container footer={Footer}>
          <Main.Title>
            <FormattedMessage
              id="boarding.signIn.title"
              defaultMessage="Sign in"
              values={{}}
            />
          </Main.Title>

          {organizations.length > 0 && (
            <>
              <Text.Section className="margin-bottom-8">
                <FormattedMessage
                  id="boarding.signIn.alreadySignedIn"
                  defaultMessage="You're already signed in to:"
                  values={{}}
                />
              </Text.Section>
              {organizations.map((organization) => (
                <div
                  key={organization.id}
                  className="flexbox flexbox-align-center margin-vertical-12 position-relative child-effects-on-hover"
                >
                  {organization.photoURL && (
                    <Avatar
                      organizationName={organization.name}
                      organizationPhotoUrl={organization.photoURL}
                      className="margin-right-12"
                    />
                  )}
                  <div className="flexbox flexbox-column flexbox-grow">
                    <div className="font-weight-bold shade-05 margin-bottom-4">
                      {organization.name}
                    </div>
                    <div className="shade-20">
                      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                      {`${organization.domain}.${productDomain}`}
                    </div>
                  </div>

                  <ButtonHTMLLink
                    variant="primary"
                    href={`${urls.getOrganizationAppPath(
                      {
                        appDomain: productDomain,
                        appProtocol,
                        appPath: superEnabled ? superPath : appPath,
                      },
                      organization.domain
                    )}${
                      structureCacheKey
                        ? `?structureCacheKey=${structureCacheKey}`
                        : ''
                    }`}
                    className="margin-left-12 radius-large"
                    disabled={isNavigating}
                    icon={isNavigating ? SpinnerIcon : undefined}
                    iconClassName={isNavigating ? 'spinner-rotating' : ''}
                    onClick={() => {
                      if (isAskXDesktop()) {
                        setItemSafe('askxsubdomain', organization.domain)
                      }
                      setIsNavigating(true)
                    }}
                  >
                    <FormattedMessage
                      id="boarding.sign-in.open"
                      defaultMessage="Open"
                    />
                  </ButtonHTMLLink>
                  <div
                    className="pointer position-absolute padding-small radius-large shade-30 line-height-one shade-20-on-hover background-shade-50-on-hover opacity-null opaque-child-effect"
                    style={{ right: -32 }}
                    onClick={() => {
                      discardOrg(organization.id)
                    }}
                  >
                    <CloseIcon size={12} />
                  </div>
                </div>
              ))}
              <HorizontalRule />
            </>
          )}

          <SelectByEmail />
          <HorizontalRule />
          <SelectByDomain superEnabled={superEnabled} />
        </Main.Container>
      </Layout>
    </>
  )
}
